// Public terms of service. Mirrors the layout convention used by
// PrivacyPage / FollowJourneyPage: max-width 720px container, dark theme
// via CSS variables with fallbacks, Hanken Grotesk + Fraunces fonts.

export default function TermsPage() {
  const styles = {
    page: {
      minHeight: '100vh',
      backgroundColor: 'var(--bg-primary, #0f0f12)',
      color: 'var(--text-primary, #e8e6e1)',
      fontFamily: "'Hanken Grotesk', 'Inter', system-ui, sans-serif",
    },
    container: {
      maxWidth: '720px',
      margin: '0 auto',
      padding: '40px 24px 96px',
    },
    backLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      color: 'var(--text-secondary, #a8a49e)',
      fontSize: '14px',
      fontWeight: '500',
      textDecoration: 'none',
      marginBottom: '40px',
      transition: 'color 0.15s',
    },
    title: {
      fontFamily: "'Fraunces', serif",
      fontSize: 'clamp(32px, 4.8vw, 44px)',
      fontWeight: '500',
      lineHeight: '1.1',
      letterSpacing: '-0.02em',
      margin: '0 0 12px',
    },
    effective: {
      fontSize: '14px',
      color: 'var(--text-muted, #8f8b82)',
      marginBottom: '32px',
    },
    intro: {
      fontSize: '15px',
      lineHeight: '1.75',
      color: 'var(--text-secondary, #a8a49e)',
      marginBottom: '40px',
    },
    section: {
      marginBottom: '40px',
    },
    h2: {
      fontFamily: "'Fraunces', serif",
      fontSize: '22px',
      fontWeight: '500',
      lineHeight: '1.3',
      color: 'var(--text-primary, #e8e6e1)',
      margin: '0 0 16px',
    },
    p: {
      fontSize: '15px',
      lineHeight: '1.75',
      color: 'var(--text-secondary, #a8a49e)',
      margin: '0 0 14px',
    },
    pCaps: {
      fontSize: '13px',
      lineHeight: '1.75',
      color: 'var(--text-secondary, #a8a49e)',
      margin: '0 0 14px',
      letterSpacing: '0.01em',
    },
    ul: {
      paddingLeft: '20px',
      margin: '0 0 14px',
      color: 'var(--text-secondary, #a8a49e)',
      fontSize: '15px',
      lineHeight: '1.75',
    },
    inlineLabel: {
      fontWeight: '600',
      color: 'var(--text-primary, #e8e6e1)',
    },
    contactBlock: {
      marginTop: '12px',
      padding: '16px 20px',
      background: 'var(--bg-secondary, #1a1a21)',
      border: '1px solid var(--border-soft, rgba(255,255,255,0.06))',
      borderRadius: '8px',
      color: 'var(--text-secondary, #a8a49e)',
      fontSize: '15px',
      lineHeight: '1.7',
    },
    link: {
      color: 'var(--accent-primary, #2dd4bf)',
      textDecoration: 'none',
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <a href="/" style={styles.backLink}>← Back to AdaptOS</a>

        <h1 style={styles.title}>Terms of Service</h1>
        <p style={styles.effective}>Effective date: June 1, 2026</p>

        <p style={styles.intro}>
          These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of AdaptOS
          at adaptos.app, operated by AdaptOS, Inc. (&ldquo;AdaptOS,&rdquo; &ldquo;we,&rdquo;
          &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By creating an account or using the platform,
          you agree to these Terms.
        </p>

        <section style={styles.section}>
          <h2 style={styles.h2}>1. Eligibility</h2>
          <p style={styles.p}>
            You must be at least 13 years old to use AdaptOS. By using the platform, you
            represent that you meet this requirement.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>2. Account Registration</h2>
          <p style={styles.p}>You agree to:</p>
          <ul style={styles.ul}>
            <li>Provide accurate registration information</li>
            <li>Keep your password secure</li>
            <li>Notify us at <a href="mailto:info@adaptos.app" style={styles.link}>info@adaptos.app</a> if you suspect unauthorized access</li>
            <li>Take responsibility for all activity under your account</li>
          </ul>
          <p style={styles.p}>
            We reserve the right to suspend or terminate accounts that violate these Terms.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>3. Subscription and Billing</h2>
          <p style={styles.p}>
            AdaptOS is a monthly subscription service. Current pricing is available at
            adaptos.app. By subscribing, you authorize us to charge your payment method on
            a recurring monthly basis until you cancel.
          </p>
          <p style={styles.p}>
            <span style={styles.inlineLabel}>Cancellation:</span> cancel at any time through account settings. Cancellation
            takes effect at the end of the current billing period. We do not provide refunds
            for partial billing periods.
          </p>
          <p style={styles.p}>
            <span style={styles.inlineLabel}>Payment processing:</span> all payments are processed by Stripe. AdaptOS does
            not store your card information.
          </p>
          <p style={styles.p}>
            <span style={styles.inlineLabel}>Pre-launch cohort pricing:</span> if you enrolled at a promotional rate, that
            rate is locked for the period specified at enrollment, after which standard
            pricing applies.
          </p>
          <p style={styles.p}>
            <span style={styles.inlineLabel}>Failed payments:</span> if payment cannot be collected, account access may be
            suspended until the balance is resolved.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>4. Free Lesson Access</h2>
          <p style={styles.p}>
            AdaptOS provides free access to the first three lessons without requiring a
            payment method. Free access is for individual evaluation only. Automated access,
            systematic content copying, or commercial use of free access is prohibited.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>5. Affiliate Program</h2>
          <p style={styles.p}>
            Participation in the AdaptOS affiliate program is governed by the Affiliate
            Terms at <a href="/affiliate" style={styles.link}>adaptos.app/affiliate</a>, incorporated here by reference. AdaptOS
            reserves the right to modify affiliate terms with reasonable notice.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>6. Acceptable Use</h2>
          <p style={styles.p}>You agree not to:</p>
          <ul style={styles.ul}>
            <li>Use the platform for unlawful purposes</li>
            <li>Reverse-engineer, decompile, or extract platform source code</li>
            <li>Scrape or systematically extract platform content or learner data</li>
            <li>Use bots or automated tools to access the platform</li>
            <li>Share account credentials or resell platform access</li>
            <li>Attempt to circumvent mastery gates, checkpoints, or access controls</li>
            <li>Interfere with platform infrastructure or security</li>
          </ul>
          <p style={styles.p}>
            Violations may result in immediate account suspension without refund.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>7. Intellectual Property</h2>
          <p style={styles.p}>
            All platform content — including lesson materials, curriculum structure, quiz
            content, pedagogical frameworks, adaptive learning algorithms, branding, and
            software — is owned by or licensed to AdaptOS, Inc.
          </p>
          <p style={styles.p}>
            You receive a limited, non-exclusive, non-transferable license to access the
            platform for personal, non-commercial educational use during an active
            subscription. This license does not include the right to copy, distribute, or
            create derivative works from platform content.
          </p>
          <p style={styles.p}>
            Your learning data (quiz results, progress, notes) remains yours. We claim no
            ownership over content you create within the platform.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>8. No Certification Guarantee</h2>
          <p style={styles.p}>
            AdaptOS is a learning tool. We do not guarantee that use of the platform will
            result in passing any certification examination. Certification outcomes depend on
            individual effort, exam conditions, and factors outside our control. Salary and
            career data displayed on the platform is sourced from third-party research and is
            provided for informational context only. Individual results will vary.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>9. Disclaimer of Warranties</h2>
          <p style={styles.pCaps}>
            THE PLATFORM IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE.&rdquo; TO THE
            FULLEST EXTENT PERMITTED BY LAW, ADAPTOS DISCLAIMS ALL WARRANTIES, EXPRESS OR
            IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            AND NON-INFRINGEMENT.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>10. Limitation of Liability</h2>
          <p style={styles.pCaps}>
            TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, ADAPTOS, INC. AND ITS OFFICERS,
            EMPLOYEES, AND SERVICE PROVIDERS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. OUR TOTAL LIABILITY FOR ANY CLAIM WILL
            NOT EXCEED THE AMOUNT YOU PAID TO US IN THE THREE MONTHS PRECEDING THE CLAIM.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>11. Governing Law</h2>
          <p style={styles.p}>
            These Terms are governed by the laws of the State of Delaware, without regard to
            conflict-of-law provisions. Any dispute will be resolved in the state or federal
            courts located in Tarrant County, Texas. Before initiating legal action, contact
            <a href="mailto:info@adaptos.app" style={styles.link}> info@adaptos.app</a> to attempt good-faith resolution.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>12. Changes to These Terms</h2>
          <p style={styles.p}>
            We will notify you of material changes by email or platform notice at least 14
            days before they take effect. Continued use after the effective date constitutes
            acceptance.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>13. Contact</h2>
          <div style={styles.contactBlock}>
            AdaptOS, Inc.<br />
            Fort Worth, Texas<br />
            <a href="mailto:info@adaptos.app" style={styles.link}>info@adaptos.app</a>
          </div>
        </section>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300..600&family=Hanken+Grotesk:wght@300;400;500;600;700&display=swap');
        a:hover { opacity: 0.85; }
      `}</style>
    </div>
  );
}
